import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, type AdminUserRow } from '@/services/adminApi';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { useAdminExportCsv } from '@/hooks/admin/useAdminExportCsv';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';
import { SortableTableHeader, type SortDir } from '@/components/admin/SortableTableHeader';
import { UserQuickActions } from '@/components/admin/UserQuickActions';
import { BulkActionsBar } from '@/components/admin/BulkActionsBar';
import { BulkDeleteDialog } from '@/components/admin/BulkDeleteDialog';
import { useAuth } from '@/contexts/AuthContext';

/**
 * AdminUsersPage — cross-tenant view of every Firestore user enriched
 * with Firebase Auth claims (sudo, emailVerified, disabled, lastSignIn).
 *
 * Admin SUPER-MVP M2 (27/05/26): le azioni grant/revoke sudo + tutte le
 * altre azioni utente (reset password, disable/enable, impersonate,
 * modifica dettagli) sono ora nel dropdown UserQuickActions per riga,
 * sostituendo il vecchio bottone singolo "Grant/Revoke super admin".
 */
export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [accountType, setAccountType] = useState('all');
  const [hasSudoFilter, setHasSudoFilter] = useState('all'); // 'all' | 'true' | 'false'
  const [searchServer, setSearchServer] = useState('');
  const [searchInline, setSearchInline] = useState('');

  // Sprint admin_sort_all_tables (30/05/26): sortBy/sortDir state
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSortChange = (field: string | null, dir: SortDir) => {
    setSortBy(field);
    setSortDir(dir);
  };

  const filterParams = useMemo(() => ({
    ...(accountType !== 'all' ? { accountType } : {}),
    ...(hasSudoFilter !== 'all' ? { hasSudo: hasSudoFilter === 'true' } : {}),
    ...(searchServer ? { search: searchServer } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
  }), [accountType, hasSudoFilter, searchServer, sortBy, sortDir]);

  const { exportCsv, isExporting } = useAdminExportCsv('users', filterParams);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.users.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<AdminUserRow>(fetchPage, [filterParams]);

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      r.email.toLowerCase().includes(s)
      || r.displayName.toLowerCase().includes(s)
      || r.uid.toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  // Bulk select (Admin SUPER-MVP M3, 27/05/26). Esclude SEMPRE l'uid corrente
  // dalle selezioni (anche da "select all") perché bulk delete API rifiuta self.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const selectableIds = useMemo(
    () => new Set(filtered.filter((r) => r.uid !== currentUser?.uid).map((r) => r.uid)),
    [filtered, currentUser?.uid],
  );
  const allSelectableSelected = selectableIds.size > 0 && Array.from(selectableIds).every((id) => selectedIds.has(id));
  const someSelectableSelected = Array.from(selectableIds).some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectableSelected) {
        for (const id of selectableIds) next.delete(id);
      } else {
        for (const id of selectableIds) next.add(id);
      }
      return next;
    });
  };

  const toggleRowSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (accountType !== 'all') activeFilters.push({ key: 'at', label: `Tipo: ${accountType}`, onClear: () => setAccountType('all') });
  if (hasSudoFilter !== 'all') activeFilters.push({ key: 'sudo', label: hasSudoFilter === 'true' ? 'Solo super admin' : 'Solo non-admin', onClear: () => setHasSudoFilter('all') });
  if (searchServer) activeFilters.push({ key: 'search-srv', label: `Ricerca server: "${searchServer}"`, onClear: () => setSearchServer('') });
  if (searchInline) activeFilters.push({ key: 'search', label: `Ricerca: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setAccountType('all'); setHasSudoFilter('all'); setSearchServer(''); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Utenti"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        onExportCsv={exportCsv}
        isExporting={isExporting}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Cerca per email, nome, uid..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[280px]"
        />
        <Input
          placeholder="Ricerca server (cross-tenant)..."
          value={searchServer}
          onChange={(e) => setSearchServer(e.target.value)}
          className="h-9 w-[240px]"
        />
        <Select value={accountType} onValueChange={setAccountType}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Account type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualsiasi tipo</SelectItem>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="agency_owner">Agency owner</SelectItem>
            <SelectItem value="member">Member</SelectItem>
          </SelectContent>
        </Select>
        <Select value={hasSudoFilter} onValueChange={setHasSudoFilter}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Super admin" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualsiasi ruolo</SelectItem>
            <SelectItem value="true">Solo super admin</SelectItem>
            <SelectItem value="false">Solo non-admin</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">Errore: {error}</p>}

      <BulkActionsBar
        count={selectedIds.size}
        entityLabel="utenti"
        onClearSelection={() => setSelectedIds(new Set())}
        onBulkDelete={() => setBulkDialogOpen(true)}
      />

      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-3 px-4 w-[40px]">
                    <Checkbox
                      checked={allSelectableSelected ? true : someSelectableSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Seleziona tutti"
                      disabled={selectableIds.size === 0}
                    />
                  </th>
                  <th className="text-left py-3 px-4">
                    <SortableTableHeader label="Utente" field="email" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Account type" field="accountType" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Aziende</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Stato</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Creato" field="createdAt" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} />
                  </th>
                  <th className="text-right font-medium text-muted-foreground py-3 px-3 w-[60px]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">Caricamento...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">Nessun utente corrisponde ai filtri attuali.</td></tr>
                )}
                {filtered.map((row) => {
                  const isSelf = currentUser?.uid === row.uid;
                  const selected = selectedIds.has(row.uid);
                  return (
                    <tr key={row.uid} className={`border-b border-border/30 last:border-0 ${selected ? 'bg-primary/5' : 'hover:bg-accent/40'}`}>
                      <td className="py-3 px-4">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleRowSelect(row.uid)}
                          disabled={isSelf}
                          aria-label={`Seleziona ${row.email}`}
                          title={isSelf ? 'Non puoi selezionare te stesso' : undefined}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/users/${row.uid}`}
                            className="font-medium hover:underline"
                          >
                            {row.displayName || '(senza nome)'}
                          </Link>
                          {row.hasSudo && (
                            <Badge variant="destructive" className="gap-1">
                              <ShieldCheck className="h-3 w-3" /> Super admin
                            </Badge>
                          )}
                          {isSelf && (
                            <Badge variant="secondary" className="text-xs">Tu</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate max-w-[280px]" title={row.uid}>
                          {row.email || row.uid}
                        </p>
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {row.accountType ? (
                          <span className="text-xs">{row.accountType}</span>
                        ) : '—'}
                        {row.agencyPlanId && (
                          <p className="text-xs text-muted-foreground">{row.agencyPlanId}</p>
                        )}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">
                        {row.membershipsCount > 0 ? (
                          <CompanyLinks
                            ids={row.membershipCompanyIds}
                            names={row.membershipCompanyNames}
                          />
                        ) : '—'}
                      </td>
                      <td className="py-3 px-3 text-xs">
                        {row.disabled ? (
                          <span className="text-destructive">disabilitato</span>
                        ) : row.emailVerified ? (
                          <span className="text-success">verificato</span>
                        ) : (
                          <span className="text-muted-foreground">non verificato</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground text-xs">
                        {row.createdAt ? new Date(row.createdAt).toLocaleDateString('it-IT') : '—'}
                        {row.lastSignInAt && (
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                            Login: {new Date(row.lastSignInAt).toLocaleDateString('it-IT')}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <UserQuickActions
                          row={row}
                          selfUid={currentUser?.uid}
                          onUpdated={refresh}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <AdminListFooter
            loadedCount={items.length}
            filteredCount={searchInline ? filtered.length : undefined}
            totalCount={totalCount}
            entityLabel="utente"
            entityLabelPlural="utenti"
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </CardContent>
      </Card>
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}

      <BulkDeleteDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        entityLabel="utenti"
        count={selectedIds.size}
        ids={Array.from(selectedIds)}
        onConfirm={async (reason) => {
          const result = await adminApi.users.bulkDelete(Array.from(selectedIds), reason);
          setSelectedIds(new Set());
          refresh();
          return { deleted: result.summary.deleted, failed: result.summary.failed };
        }}
      />
    </div>
  );
}

/**
 * Compact inline list of company links (max 3, then "+N more"). Each
 * link navigates to the company detail page.
 *
 * Mostra il NOME della company quando disponibile dal BE (sprint
 * admin_users_company_names 30/05/26 — fix bug "vedo ID invece di nome").
 * Fallback a ID troncato se il nome non è risolto (es. company cancellata
 * ma membership stale).
 */
function CompanyLinks({ ids, names }: { ids: string[]; names?: Record<string, string> }) {
  const max = 3;
  const visible = ids.slice(0, max);
  const overflow = ids.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((cid) => {
        const name = names?.[cid];
        return (
          <Link
            key={cid}
            to={`/admin/customers/${cid}`}
            className={`text-xs px-1.5 py-0.5 rounded bg-muted hover:bg-accent transition-colors ${
              name ? '' : 'font-mono'
            }`}
            title={name ? `${name} (${cid})` : cid}
          >
            {name ?? `${cid.slice(0, 8)}…`}
          </Link>
        );
      })}
      {overflow > 0 && (
        <span className="text-xs text-muted-foreground">+{overflow}</span>
      )}
    </div>
  );
}
